import { css } from 'aphrodite';
import moment from 'moment';
import { useState } from 'react';
import { Control, Controller, FormState } from 'react-hook-form';
import { Button } from '../../../../../../../web/components-v2/Button';
import { Calendar } from '../../../../../../../web/components-v2/Calendar';
import { Input } from '../../../../../../../web/components-v2/Input';
import { MASK_PRESETS, MaskedInput } from '../../../../../../../web/components-v2/MaskedInput';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../../../../web/components-v2/Popover';
import { CalendarIcon } from '../../../../../../../web/components/svgs/icons/CalendarIcon';
import { CountrySelector } from '../../../../../../../web/components/ValueSelectors/CountrySelector';
import { baseStyleSheet } from '../../../../../../../web/styles/styles';
import { IContactCollectingSurvey } from '../../../../../../models/Api';
import { stripNanpCountryCode } from '../../../../../../../models/utils/phoneUtils';
import { ErrorMessage } from './ErrorMessage';
import { styleSheet } from './styles';
import type { IFormValues } from './types';

/** Format birthday values are stored/submitted in. */
const BIRTHDAY_FORMAT = 'MM/DD/YYYY';

interface ContactInfoStepProps {
	/** The survey configuration, used to determine which contact fields to collect and which are required. */
	survey: IContactCollectingSurvey;
	/** The react-hook-form control object for registering controlled inputs. */
	control: Control<IFormValues>;
	/** The react-hook-form formState object, used to access field errors. */
	formState: FormState<IFormValues>;
	/**
	 * When true, render field-level validation errors. True after a full submit OR after a
	 * "Continue" attempt on the partial contact step — the latter doesn't set `formState.isSubmitted`
	 * (Continue is a `type='button'`, not a form submit), so gating on `isSubmitted` alone would
	 * silently swallow contact-field errors and the Continue click would appear to do nothing.
	 */
	showErrors: boolean;
}

/**
 * Renders the respondent contact-info fields (name, email, phone, birthday, address)
 * based on the survey's `options` configuration.
 *
 * Shared between the V1 single-page form and the V2 multi-step contact step.
 * Each field is conditionally shown only when the corresponding `collect` flag is set on the survey.
 */
export const ContactInfoStep = ({ survey, control, formState, showErrors }: ContactInfoStepProps) => {
	const [isBirthdayCalendarOpen, setIsBirthdayCalendarOpen] = useState(false);

	return (
		<>
			{survey.options?.contactNameOptions?.collect ? (
				<>
					<div className={css(styleSheet.formControl)}>
						<label htmlFor='client-firstName' className={css(styleSheet.label)}>
							First Name{' '}
							{survey.options.contactNameOptions.require ? (
								<span className={css(baseStyleSheet.required)}>*</span>
							) : null}
						</label>
						<Controller
							name='firstName'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<Input id='client-firstName' type='text' aria-label='First Name' ref={ref} {...fieldProps} />
							)}
						/>
						{showErrors && formState.errors.firstName && <ErrorMessage message={formState.errors.firstName.message} />}
					</div>

					<div className={css(styleSheet.formControl)}>
						<label htmlFor='client-lastName' className={css(styleSheet.label)}>
							Last Name{' '}
							{survey.options.contactNameOptions.require ? (
								<span className={css(baseStyleSheet.required)}>*</span>
							) : null}
						</label>
						<Controller
							name='lastName'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<Input id='client-lastName' type='text' ref={ref} {...fieldProps} />
							)}
						/>
						{showErrors && formState.errors.lastName && <ErrorMessage message={formState.errors.lastName.message} />}
					</div>
				</>
			) : null}

			{survey.options?.contactEmailOptions?.collect ? (
				<div className={css(styleSheet.formControl)}>
					<label htmlFor='client-email' className={css(styleSheet.label)}>
						Email{' '}
						{survey?.options.contactEmailOptions.require ? (
							<span className={css(baseStyleSheet.required)}>*</span>
						) : null}
					</label>
					<Controller
						name='email'
						control={control}
						render={({ field: { ref, ...fieldProps } }) => (
							<Input id='client-email' type='text' ref={ref} {...fieldProps} />
						)}
					/>
					{showErrors && formState.errors.email && <ErrorMessage message={formState.errors.email.message} />}
				</div>
			) : null}
			{survey.options?.contactPhoneNumberOptions?.collect ? (
				<div className={css(styleSheet.formControl)}>
					<label htmlFor='client-phone' className={css(styleSheet.label)}>
						Phone{' '}
						{!survey?.options.contactPhoneNumberOptions.require ? (
							<small className={css(styleSheet.optionalLabel)}>(Optional)</small>
						) : (
							<span className={css(baseStyleSheet.required)}>*</span>
						)}
					</label>
					<Controller
						name='phoneNumber'
						control={control}
						render={({ field: { value, onChange, onBlur, name } }) => (
							// US phone mask with international passthrough: typing or pasting a leading `+`
							// bypasses the mask and keeps the raw `+<countrycode>…` value verbatim.
							// No `ref` forwarded (unlike birthday): the form's iframe-safe scrollToFirstError
							// owns post-submit focus, so we must not let RHF's shouldFocusError grab this field.
							<MaskedInput
								{...MASK_PRESETS.phoneUS}
								id='client-phone'
								name={name}
								value={value ?? ''}
								onBlur={onBlur}
								onValueChange={change => onChange(change.formatted)}
								normalizePaste={stripNanpCountryCode}
								passthroughPrefix='+'
								styles={[styleSheet.maskedInput]}
							/>
						)}
					/>
					{showErrors && formState.errors.phoneNumber && (
						<ErrorMessage message={formState.errors.phoneNumber.message} />
					)}
				</div>
			) : null}
			{survey.options?.contactBirthdayOptions?.collect ? (
				<div className={css(styleSheet.formControl)}>
					<label htmlFor='client-birthday' className={css(styleSheet.label)}>
						Birthday{' '}
						{survey?.options.contactBirthdayOptions.require ? (
							<span className={css(baseStyleSheet.required)}>*</span>
						) : (
							<small className={css(styleSheet.optionalLabel)}>(Optional)</small>
						)}
					</label>
					<Controller
						name='birthday'
						control={control}
						render={({ field: { ref, value, onChange, onBlur, name } }) => {
							// Drive the calendar from the current field value when it is a complete, valid date.
							const parsedBirthday = moment(value, BIRTHDAY_FORMAT, true);
							const selectedDate = parsedBirthday.isValid() ? parsedBirthday.toDate() : undefined;
							// Bound the calendar to the same window the yup validation enforces — no future
							// dates, nothing older than 120 years — so a picked day can never fail validation.
							const today = new Date();
							const earliestBirthday = moment().subtract(120, 'years').toDate();
							// Reuse the legacy `.text-input` shell so the field matches every other input
							// (gray fill, border, radius). MaskedInput handles MM/DD/YYYY formatting and
							// caret preservation; `birthdayMaskInput` strips its components-v2 chrome so it
							// blends into the shell, and the calendar icon sits inside on the right.
							return (
								<div className={`text-input ${css(styleSheet.birthdayField)}`}>
									<MaskedInput
										{...MASK_PRESETS.dateMDY}
										id='client-birthday'
										name={name}
										ref={ref}
										value={value ?? ''}
										onBlur={onBlur}
										onValueChange={change => onChange(change.formatted)}
										styles={[styleSheet.birthdayMaskInput]}
									/>
									<Popover open={isBirthdayCalendarOpen} onOpenChange={setIsBirthdayCalendarOpen}>
										<PopoverTrigger asChild>
											<Button
												type='button'
												variant='ghost'
												size='icon'
												aria-label='Open calendar'
												styles={[styleSheet.birthdayCalendarButton]}
											>
												<CalendarIcon width={18} height={18} />
											</Button>
										</PopoverTrigger>
										<PopoverContent side='bottom' align='end'>
											<div data-testid='birthday-calendar'>
												<Calendar
													mode='single'
													// Birthdays span decades — month/year dropdowns beat paging by chevron.
													captionLayout='dropdown'
													startMonth={earliestBirthday}
													endMonth={today}
													// Two matchers (OR), not one interval: disable everything before the
													// earliest allowed birthday and everything after today.
													disabled={[{ before: earliestBirthday }, { after: today }]}
													selected={selectedDate}
													defaultMonth={selectedDate}
													onSelect={day => {
														// Only commit + close when a day was actually picked. Clicking the
														// already-selected day fires onSelect(undefined); leave the value
														// untouched and keep the popover open in that case.
														if (day) {
															onChange(moment(day).format(BIRTHDAY_FORMAT));
															setIsBirthdayCalendarOpen(false);
														}
													}}
												/>
											</div>
										</PopoverContent>
									</Popover>
								</div>
							);
						}}
					/>
					{showErrors && formState.errors.birthday && <ErrorMessage message={formState.errors.birthday.message} />}
				</div>
			) : null}

			{survey.options?.contactAddressOptions?.collect ? (
				<div className={css(styleSheet.addressSection)}>
					<div className={css(styleSheet.formControl)}>
						<label htmlFor='client-address1' className={css(styleSheet.label)}>
							Address Line 1{' '}
							{survey?.options.contactAddressOptions.require ? (
								<span className={css(baseStyleSheet.required)}>*</span>
							) : (
								<small className={css(styleSheet.optionalLabel)}>(Optional)</small>
							)}
						</label>
						<Controller
							name='address1'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<Input id='client-address1' type='text' placeholder='Street Address' ref={ref} {...fieldProps} />
							)}
						/>
						{showErrors && formState.errors.address1 && <ErrorMessage message={formState.errors.address1.message} />}
					</div>

					<div className={css(styleSheet.formControl)}>
						<label htmlFor='client-address2' className={css(styleSheet.label)}>
							Address Line 2 <small className={css(styleSheet.optionalLabel)}>(Optional)</small>
						</label>
						<Controller
							name='address2'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<Input
									id='client-address2'
									type='text'
									placeholder='Apartment, suite, etc.'
									ref={ref}
									{...fieldProps}
								/>
							)}
						/>
					</div>

					<div className={css(styleSheet.formControl)}>
						<div className={css(styleSheet.addressFieldRow)}>
							<div className={css(styleSheet.addressColumn)}>
								<label htmlFor='client-city' className={css(styleSheet.label)}>
									City{' '}
									{survey?.options.contactAddressOptions.require ? (
										<span className={css(baseStyleSheet.required)}>*</span>
									) : (
										<small className={css(styleSheet.optionalLabel)}>(Optional)</small>
									)}
								</label>
								<Controller
									name='city'
									control={control}
									render={({ field: { ref, ...fieldProps } }) => (
										<Input id='client-city' type='text' placeholder='City' ref={ref} {...fieldProps} />
									)}
								/>
								{showErrors && formState.errors.city && <ErrorMessage message={formState.errors.city.message} />}
							</div>
							<div className={css(styleSheet.addressColumn)}>
								<label htmlFor='client-state' className={css(styleSheet.label)}>
									State/Province{' '}
									{survey?.options.contactAddressOptions.require ? (
										<span className={css(baseStyleSheet.required)}>*</span>
									) : (
										<small className={css(styleSheet.optionalLabel)}>(Optional)</small>
									)}
								</label>
								<Controller
									name='stateProvince'
									control={control}
									render={({ field: { ref, ...fieldProps } }) => (
										<Input id='client-state' type='text' placeholder='State/Province' ref={ref} {...fieldProps} />
									)}
								/>
								{showErrors && formState.errors.stateProvince && (
									<ErrorMessage message={formState.errors.stateProvince.message} />
								)}
							</div>
							<div className={css(styleSheet.addressColumn)}>
								<label htmlFor='client-postalCode' className={css(styleSheet.label)}>
									Postal Code{' '}
									{survey?.options.contactAddressOptions.require ? (
										<span className={css(baseStyleSheet.required)}>*</span>
									) : (
										<small className={css(styleSheet.optionalLabel)}>(Optional)</small>
									)}
								</label>
								<Controller
									name='postalCode'
									control={control}
									render={({ field: { ref, ...fieldProps } }) => (
										<Input id='client-postalCode' type='text' placeholder='Postal Code' ref={ref} {...fieldProps} />
									)}
								/>
								{showErrors && formState.errors.postalCode && (
									<ErrorMessage message={formState.errors.postalCode.message} />
								)}
							</div>
						</div>
					</div>

					<div className={css(styleSheet.formControlFullWidth, styleSheet.countrySelectorContainer)}>
						<label id='client-country-label' className={css(styleSheet.label)}>
							Country{' '}
							{survey?.options.contactAddressOptions.require ? (
								<span className={css(baseStyleSheet.required)}>*</span>
							) : (
								<small className={css(styleSheet.optionalLabel)}>(Optional)</small>
							)}
						</label>
						<Controller
							name='country'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<div
									id='client-country'
									tabIndex={-1}
									role='combobox'
									aria-labelledby='client-country-label'
									aria-controls='country-options'
									aria-expanded='false'
								>
									<CountrySelector
										selectedValue={fieldProps.value}
										onSelectionChanged={selected => fieldProps.onChange(selected.value)}
										className={css(styleSheet.countrySelector)}
									/>
								</div>
							)}
						/>
						{showErrors && formState.errors.country && <ErrorMessage message={formState.errors.country.message} />}
					</div>
				</div>
			) : null}
		</>
	);
};
